//
// SELECTORS, these are functions designed to work directly on and with the
// redux data store
//

import { createSelector } from '@reduxjs/toolkit';
import { List as ImmutableList, Map } from 'immutable';
import { DateTime } from 'luxon';
import { LruObjectCache, createCachedSelector } from 're-reselect';

import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { scheduledTransactionsSelectors } from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import { getLogger } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';

import { getOptimalCacheFromObject, setOptimalCacheFromObject } from 'data/optimalCacheManager';
import { memoizeWithKey, memoizeWithKeyRetrieveCache } from 'utils/memoizeWithKey';
import { getOrCreateStringFromObject } from 'utils/objectToKeyUtils';

import { balanceSelectors } from 'app-components/flux/calculated-balances';

import { optimalCacheCompareFn, optimalCacheProcessFn } from 'data/transactionList/optimalCacheFunctions';

import { dateSelectors } from 'app-components/flux/date';
import { groupTxnListByAmount, groupTxnListByDate, groupTxnListByString } from './grouping';
import { sortTransactions } from './sorting';

const log = getLogger('data/transactionList/selectors');

//-----------------------------------------------
//-----------------------------------------------

// getTransactionListForAccountIds
//
// This selector expects the following properties:
//   - an array of accountIds
//   - sortBy property
//   - sortOrder property
//   - filter function that if defined is provided the transaction list to filter. null
//     functions as the identity filter (show all)
//
// Returns an immutable list of transactions from accounts specified by the
// accountIds array.

export const getIsProtectedTxn = createSelector(
  accountsSelectors.getQBPAccountIds,
  (state, props) => props.txn,
  (qbpAccountIds, txn) => {
    if (!txn) return false;

    return qbpAccountIds?.includes(txn.accountId);
  },
);

export const getTotalNumberOfSplitsWithAllocations = createSelector(
  (state, props) => props.txn,
  (state) => transactionsSelectors.getTransactionsByAccountId(state),
  (txn, txns) => {
    if (!txn || !txns) return 0;
    if (!txn.split) return 0;

    return txn.split.items.reduce((acc, split) => {
      const transferTx = transactionsUtils.findMatchingTransferTx(
        txn,
        txns.get(split.coa.id),
        split.amount,
        split.transfer,
      );
      return acc + (transferTx?.txn?.allocations?.size > 0 ? 1 : 0);
    }, 0);
  },
);

export const getTransactionListForAccountIds = createSelector(
  (state, props) => props.accountIds,
  (state, props) => props.sortBy || 'date',
  (state, props) => props.sortOrder || 'sortDescending',
  (state, props) => props.filterFn,
  (state, props) => props.overrideTxns,
  (state, props) => props.id,
  transactionsSelectors.getTransactionsStore,
  accountsSelectors.getAccountsById,
  transactionsSelectors.getTransactionsByAccountId,
  transactionsSelectors.getTransactionsByAccountIdsSelectors,
  balanceSelectors.getBalancesByAccountIds,
  featureFlagsSelectors.getFeatureFlags,

  (function combinerClosure() {
    return (
      accountIds,
      sortBy,
      sortOrder,
      filterFn,
      overrideTxns,
      id,
      transactionsStore,
      accountsById,
      transactionsByAccountIds,
      transactionsByAccountIdsSelectors,
      balancesById,
      featureFlags,
    ) => {
      const cacheObject = {
        name: 'txList',
        accountIds,
        sortBy,
        sortOrder,
        filterFn,
        overrideTxns,
        id,
        dateOnAfter: transactionsStore.dateOnAfter,
        accountsById,
        transactionsByAccountIdsSelectors,
        featureFlags,
      };
      const strictCacheObject = {
        ...cacheObject,
        transactionsByAccountIds,
        balancesById,
      };
      // if we are forcing a cache break, then skip cache processing, and go straight to regeneration,
      const cachedValues = getOptimalCacheFromObject(
        transactionsStore,
        'txList',
        cacheObject,
        strictCacheObject,
        (oldTxn, newTxn) => optimalCacheCompareFn(oldTxn, newTxn, sortBy, filterFn),
        (cache, newTxn) => optimalCacheProcessFn(cache, newTxn, filterFn),
        10,
      );
      if (cachedValues) log.debug('===================== TXLIST CACHE HIT =======================');
      if (cachedValues) return cachedValues;

      log.debug('===================== TXLIST CACHE MISS =======================');

      let accountIdsList = accountIds;
      if (!accountIds || accountIds.length === 0) {
        accountIdsList = accountsById.map((x) => x.id).toList();
      }

      let transactionsForAccountIds;
      if (overrideTxns) {
        transactionsForAccountIds = overrideTxns;
      } else {
        transactionsForAccountIds = ImmutableList();

        accountIdsList.forEach((accountId) => {
          const acct = accountsById.get(accountId);

          const hideTxns = accountsUtils.transactionsNotSupportedForAccountId(
            accountId,
            featureFlags.get('hideConnectedLoanTransactions'),
            store?.getState(),
          );

          if (acct && !hideTxns) {
            const transactionsForAccountIdSelector = transactionsByAccountIdsSelectors.get(accountId);

            const transactionsForAccountId = transactionsForAccountIdSelector(transactionsByAccountIds);

            // Don't show scheduled transaction reminder in register (they go to upcoming section)
            const filteredTransactions = transactionsForAccountId.filter(
              (txn) => !transactionsUtils.isUnacceptedScheduledTxn(txn),
            );
            transactionsForAccountIds = transactionsForAccountIds.concat(filteredTransactions);
          }
        });
      }

      let filteredTxns = filterFn ? filterFn(transactionsForAccountIds) : transactionsForAccountIds;
      if (sortBy === 'reviewed') {
        filteredTxns = filteredTxns.filter((txn) => (sortOrder === 'ascending' ? !txn.isReviewed : txn.isReviewed));
      }

      // filter out any future transactions and DUE/OVERDUE scheduled transactions
      // but do not do this if caller sent in their own txn list (override txns)
      if (!overrideTxns) {
        const today = DateTime.local().toISODate();
        filteredTxns = filteredTxns.filter(
          (txn) =>
            DateTime.fromISO(txn.postedOn).toISODate() <= today && !transactionsUtils.isUnacceptedScheduledTxn(txn),
        );
      }
      const sortedTxns = sortTransactions(filteredTxns, sortBy, sortOrder);

      let groupedTxns;
      switch (sortBy) {
        // TODO: remove 'status' and 'date' case once old register is removed/updated
        case 'date':
        case 'postedOn':
          groupedTxns = groupTxnListByDate(sortedTxns, featureFlags, false, sortOrder);
          break;

        case 'status':
        case 'state':
          groupedTxns = groupTxnListByDate(sortedTxns, featureFlags, true, sortOrder);
          break;

        case 'category':
        case 'payee':
        case 'check':
          groupedTxns = groupTxnListByString(sortedTxns, sortBy);
          break;

        case 'reviewed':
          groupedTxns = groupTxnListByDate(sortedTxns, featureFlags, true, sortOrder);
          break;

        case 'amount':
          groupedTxns = groupTxnListByAmount(sortedTxns);
          break;

        case 'action':
          groupedTxns = sortedTxns.groupBy(
            (transaction) => transactionsUtils.InvestmentTxnActionsByType[transaction.investmentTxnType] || 'Others',
          );
          break;

        case 'quantity':
        case 'price':
        case 'commission':
          groupedTxns = sortedTxns.groupBy(() => '');
          break;

        default:
          groupedTxns = groupTxnListByString(sortedTxns, 'payee');
      }

      setOptimalCacheFromObject(transactionsStore, 'txList', cacheObject, strictCacheObject, groupedTxns, 10);
      return groupedTxns;
    };
  })(),
);

/*
 * GetLesserBilledTransactions
 */
export const getLesserBilledTransactions = createSelector(
  (state, props) => props.isBill,
  (state, props) => props.isTransfer,
  scheduledTransactionsSelectors.getScheduledTransactions,
  transactionsSelectors.getTransactionsByTextFilter,
  categoriesSelectors.getIncomeCOAIds,
  (isBill, isTransfer, scheduledTxns, txnList, incomeCOAs) => {
    const endDate = DateTime.local();
    const startDate = endDate.minus({ months: 6 });
    const txns = txnList.filter((txn) => {
      // currently, only cash-flow txns can be recurring
      if (txn.type !== 'CASH_FLOW') return false;

      // we also don't support split-txns
      if (transactionsUtils.isSplitTxn(txn)) return false;

      // if txn is already linked to a scheduled txn, do not include it
      if (txn.stModelId && scheduledTxns && scheduledTxns.has(txn.stModelId)) return false;

      const isIncome = transactionsUtils.isIncomeFromTxn(txn, incomeCOAs);
      if (isBill !== undefined && (isBill ? isIncome : !isIncome)) return false;

      if (isTransfer) {
        // if not an internal or external transfer and not a credit-card payment, do not include it
        if (txn?.coa?.type !== 'ACCOUNT' && txn?.coa?.id !== '2' && txn?.coa?.id !== '3') return false;

        // transactionsUtils.findMatchingTransferTxn()
      }

      const txnDate = DateTime.fromISO(txn.postedOn);
      if (txnDate < startDate || txnDate > endDate) return false;

      return true;
    });

    return sortTransactions(txns);
  },
);

// copy logic from above, but return just the ids
export const getLesserBilledTxnIds = createSelector(
  (state, props) => props.isBill,
  (state, props) => props.isTransfer,
  scheduledTransactionsSelectors.getScheduledTransactions,
  transactionsSelectors.getTransactionsByTextFilter,
  categoriesSelectors.getIncomeCOAIds,
  (isBill, isTransfer, scheduledTxns, txnList, incomeCOAs) => {
    const endDate = DateTime.local();
    const startDate = endDate.minus({ months: 6 });
    return txnList.reduce((acc, txn) => {
      // currently, only cash-flow txns can be recurring
      if (txn.type !== 'CASH_FLOW') return acc;

      // we also don't support split-txns
      if (transactionsUtils.isSplitTxn(txn)) return acc;

      // if txn is already linked to a scheduled txn, do not include it
      if (txn.stModelId && scheduledTxns && scheduledTxns.has(txn.stModelId)) return acc;

      const isIncome = transactionsUtils.isIncomeFromTxn(txn, incomeCOAs);
      if (isBill !== undefined && (isBill ? isIncome : !isIncome)) return acc;

      if (isTransfer) {
        // if not an internal or external transfer and not a credit-card payment, do not include it
        if (txn?.coa?.type !== 'ACCOUNT' && txn?.coa?.id !== '2' && txn?.coa?.id !== '3') return acc;
      }

      const txnDate = DateTime.fromISO(txn.postedOn);
      if (txnDate < startDate || txnDate > endDate) return acc;

      acc.push(txn.id);
      return acc;
    }, []);
  },
);

export const transactionListMetrics = createCachedSelector(
  (state, transactionList) => transactionList,
  (txnList) => {
    let numTxns = 0;
    let sumTxns = 0;
    let earliestDate = DateTime.local().toISODate();
    let reviewedCount = 0;
    let hasScheduledInstances = false;

    txnList.forEach((section) => {
      numTxns += section.size;
      section.forEach((txn) => {
        sumTxns += Number(txn.amount);
        if (txn.postedOn < earliestDate) {
          earliestDate = txn.postedOn;
        }
        reviewedCount += txn.isReviewed ? 1 : 0;
        hasScheduledInstances = hasScheduledInstances || transactionsUtils.isUnacceptedScheduledTxn(txn);
      });
    });
    return { numTxns, sumTxns, earliestDate, reviewedCount, hasScheduledInstances };
  },
)({
  keySelector: (state, props) => (props.accountIds ? props.accountIds.toString() : 'all'),
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getDateGroupedTransactions = createSelector(
  (txns) => txns,
  (txns, date) => date,
  (txns, date, schTxns) => schTxns,
  (function combinerClosure() {
    return (txns, date, schTxns) => {
      const key = getOrCreateStringFromObject(
        'dateGroupedTxns',
        { txns, date, schTxns },
        5,
        memoizeWithKeyRetrieveCache, // fn used for garbage collection for unusued object strings
      );
      return memoizeWithKey(
        'dateGroupedTxns',
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        () => futureFilteredTxns(txns, date, schTxns)?.groupBy((txn) => DateTime.fromISO(txn.postedOn).toISODate()),
        key,
        5,
      );
    };
  })(),
);

export const getGroupedRecentAndFutureAccountTransactions = createSelector(
  transactionsSelectors.getTransactionsByAccountIdRaw,
  dateSelectors.getCurrentDate,
  (_state, props) => props.dateRange,
  (txnsByAccountId, currentDate, dateRange) => {
    const endDate = currentDate.plus({ days: dateRange }).toISODate();
    const pastCutoff = currentDate.minus({ days: 10 }).toISODate();
    const todayDate = currentDate.toISODate();

    const cacheKey = getOrCreateStringFromObject(
      'recent-grouped-txns',
      {
        name: 'recentAcctTxns',
        pastCutoff: pastCutoff,
        txnsByAccountId,
      },
      5,
      memoizeWithKeyRetrieveCache, // fn used for garbage collection for unusued object strings
    );

    return memoizeWithKey(
      'recent-grouped-txns',
      () => {
        const todayTxnArr = []; // for due or overdue txns
        const groupedTxns = Map().withMutations((accountMapSetter) => {
          txnsByAccountId.forEach((account, key) => {
            const ungroupedTxns = account.filter((txn) => {
              if (txn.id === txn.clientId) {
                // filter out optimistically created manual transactions because they crash AMcharts
                return false;
              }
              const refundState = transactionsUtils.getRefundState(txn);
              if (
                refundState === transactionsUtils.RefundState.EXPECTED ||
                refundState === transactionsUtils.RefundState.OVERDUE
              ) {
                return true;
              }
              const txnDate = txn.postedOn;
              const info = transactionsUtils.getTxnStateInfo(txn);
              if (info.status === 'DUE' || info.status === 'OVERDUE' || transactionsUtils.isPlannedA2ATransfer(txn)) {
                if (txnDate < todayDate) {
                  todayTxnArr.push(txn);
                }
                return true;
              }
              return txnDate && txnDate < endDate && txnDate > pastCutoff;
            });
            const ret = ungroupedTxns?.groupBy((txn) => txn.postedOn);
            accountMapSetter.set(key, ret);
          });
        });
        return {
          todayTxnArr,
          groupedTxns,
        };
      },
      cacheKey,
      5,
    );

    // log.debug("REGENERATING RECENT ACCOUNT TRANSACTIONS", accountId);

    // Because of the way that the projected balances graph is situated, we want separation and grouping of transactions by account
  },
);

export const processCalendarTxns = createSelector(
  (txns) => txns,
  (txns) => txns.groupBy(() => '9999-99:Pending'),
);

/** *****************************************************************************************
 * HELPER FUNCTIONS
 ******************************************************************************************** */
// This selector is basically used for cache (could use memoize instead)
export const futureFilteredTxns = (txns, date, schTxns) =>
  txns.filter((txn) => {
    const txnDate = DateTime.fromISO(txn.postedOn);
    if (schTxns) {
      return (
        (txn.source === 'USER_ENTERED' && txnDate.diff(DateTime.local(), 'days').toObject().days >= 0) ||
        txn.source === 'SCHEDULED_TRANSACTION_PENDING'
      );
    }
    return (
      // adding this so we can have the 'waiting' button
      txn.source === 'USER_ENTERED' || txn.source === 'SCHEDULED_TRANSACTION_PENDING'
    );
  });
